import { useEffect, useRef, useState } from "react";
import { type DecryptedHistoryItem } from "../account-client";
import { ShareToggle } from "./ShareToggle";
import { CheckIcon, LinkIcon, LinkOffIcon, KebabIcon } from "./icons";

// One row in the History tab: title + meta, a share toggle (cyan on / grey off), a
// link-copy icon (active only when shared), and a ⋮ menu (Share·Unshare / Rename / Delete).
// Self-contained interaction state; the parent owns the data + the async mutators.
export function HistoryRow({
  item,
  onSetState,
  onRename,
  onDelete,
}: {
  item: DecryptedHistoryItem;
  onSetState: (state: "active" | "private") => Promise<void>;
  onRename: (title: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(item.title ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const shared = item.state === "active";
  const usable = (item.state === "active" || item.state === "private") && item.keyFragment != null;

  // Untitled entries borrow the short id as their title (a recognizable handle)
  // rather than a literal "(untitled)"; titled entries keep the id in the meta
  // line. The openable link always uses the full id, regardless of what's shown.
  const titled = !!item.title;
  const idHandle = item.shareId.slice(0, 10);
  const displayTitle = item.title || idHandle;
  const created = new Date(item.createdAt).toLocaleString();

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  function copyLink() {
    if (!shared || !item.keyFragment) return;
    const link = `${window.location.origin}/s/${item.shareId}#key=${item.keyFragment}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }

  if (item.state !== "active" && item.state !== "private") {
    // tombstoned (deleted/expired) entry still in the list until the sweeper cascade
    return (
      <div className="history-row">
        <div className="history-entry">
          <span>unavailable</span>
          <span className="history-meta">{idHandle} · {created}</span>
        </div>
        <button type="button" className="history-remove" title="Remove from history" onClick={() => onDelete()}>×</button>
      </div>
    );
  }

  return (
    <div className="history-row">
      <div className="history-entry">
        {renaming ? (
          <input
            className="history-rename-input"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void withBusy(async () => { await onRename(draft); setRenaming(false); });
              if (e.key === "Escape") { setRenaming(false); setDraft(item.title ?? ""); }
            }}
            onBlur={() => { if (!busy) setRenaming(false); }}
          />
        ) : (
          <div className="history-title-row">
            {usable ? (
              <a href={`/s/${item.shareId}#key=${item.keyFragment}`}>{displayTitle}</a>
            ) : (
              <span>{displayTitle}</span>
            )}
            {!titled && <span className="history-untitled-chip">untitled</span>}
          </div>
        )}
        <span className="history-meta">{titled ? `${idHandle} · ${created}` : created}</span>
      </div>

      {confirmDelete ? (
        <span className="history-confirm">
          Delete permanently?
          <button type="button" className="history-confirm-btn danger" disabled={busy}
            onClick={() => void withBusy(async () => { await onDelete(); })}>Confirm</button>
          <button type="button" className="history-confirm-btn" disabled={busy}
            onClick={() => setConfirmDelete(false)}>Cancel</button>
        </span>
      ) : (
        <div className="history-controls">
          <ShareToggle
            on={shared}
            busy={busy}
            disabled={busy}
            ariaLabel={shared ? "Shared (encrypted)" : "Not shared"}
            title={shared ? "Shared — link is live" : "Private — toggle to share"}
            onClick={() => void withBusy(async () => { await onSetState(shared ? "private" : "active"); })}
          />

          <button
            type="button"
            className={`history-share${copied ? " copied" : ""}${shared ? "" : " off"}`}
            onClick={copyLink}
            disabled={!shared}
            title={shared ? (copied ? "Copied" : "Copy share link") : "Not shared — toggle to share"}
            aria-label={shared ? "Copy share link" : "Not shared"}
          >
            {/* crossed chain = sharing off; check = just copied; chain = copy link */}
            {!shared ? <LinkOffIcon /> : copied ? <CheckIcon /> : <LinkIcon />}
          </button>

          <div className="row-menu-wrap" ref={menuRef}>
            <button type="button" className="row-menu-trigger" aria-haspopup="menu" aria-expanded={menuOpen}
              title="More" onClick={() => setMenuOpen((o) => !o)}>
              <KebabIcon size={18} />
            </button>
            {menuOpen && (
              <div className="row-menu" role="menu">
                <button type="button" role="menuitem" className="row-menu-item"
                  onClick={() => { setMenuOpen(false); void withBusy(async () => { await onSetState(shared ? "private" : "active"); }); }}>
                  {shared ? "Unshare" : "Share"}
                </button>
                <button type="button" role="menuitem" className="row-menu-item" disabled={!usable}
                  onClick={() => { setMenuOpen(false); setDraft(item.title ?? ""); setRenaming(true); }}>
                  Rename
                </button>
                <div className="row-menu-sep" />
                <button type="button" role="menuitem" className="row-menu-item danger"
                  onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryRow;
